// frontend/src/pages/CorrespondenceDetailPage.tsx - FULL PAGE CORRESPONDENCE DETAIL VIEW
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCorrespondence, useCorrespondenceAttachments, useCorrespondenceAI, useUpdateCorrespondence } from '@/hooks/useCorrespondences';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { formatDate, cn } from '@/utils';
import { UploadProgress } from '@/components/ui/UploadProgress';
import apiClient from '@/api';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

const CorrespondenceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();
  const [activeTab, setActiveTab] = useState<'overview' | 'attachments' | 'responses' | 'email' | 'ai'>('overview');

  const { data: correspondence, loading, error, refetch: refetchCorrespondence } = useCorrespondence(id);
  const { mutate: updateCorrespondence } = useUpdateCorrespondence();
  const [orgCustomFields, setOrgCustomFields] = useState<any[]>([]);
  const [editingCustomFields, setEditingCustomFields] = useState(false);
  const [customFieldsDraft, setCustomFieldsDraft] = useState<Record<string, any>>({});
  const [savingFields, setSavingFields] = useState(false);
  const [savingPrimary, setSavingPrimary] = useState(false);

  // Change the primary-source attachment (or revert to email body). Called from the Source section.
  const handleSetPrimaryAttachment = async (attachmentId: string | null) => {
    if (!correspondence?._id) return;
    setSavingPrimary(true);
    try {
      await apiClient.correspondences.setPrimaryAttachment(correspondence._id, attachmentId);
      addNotification?.({
        type: 'success',
        title: 'Source updated',
        message: attachmentId ? 'Primary attachment set as source' : 'Source reverted to email body'
      });
      await refetchCorrespondence();
    } catch (err: any) {
      addNotification?.({
        type: 'error',
        title: 'Could not update source',
        message: err?.response?.data?.error || err?.message || 'Unknown error'
      });
    } finally {
      setSavingPrimary(false);
    }
  };

  // Fetch org custom field definitions
  useEffect(() => {
    apiClient.organizationCustomization.getFields().then((res: any) => {
      const data = res?.data || res;
      setOrgCustomFields((data?.effectiveCorrespondenceFields || []).filter((f: any) => f.fieldId !== 'document_age_days'));
    }).catch(() => {});
  }, []);

  // Fetch attachments and AI summary from separate endpoints
  const { data: attachments, loading: loadingAttachments, refetch: refetchAttachments } = useCorrespondenceAttachments(
    correspondence?._id || null
  );

  // Letters arrive as links, or several letters share one email: the register
  // needs documents added to and removed from an entry after the fact.
  const { confirm } = useConfirmation();
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const canManageAttachments = ['admin', 'implementor', 'contributor'].includes(user?.role || '');

  const handleAddAttachments = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !correspondence?._id) return;
    setUploadingAttachments(true);
    setUploadPercent(0);
    try {
      const res: any = await apiClient.attachments.upload({
        files: Array.from(files),
        attachmentType: 'correspondence',
        correspondenceId: correspondence._id,
        onProgress: setUploadPercent
      });
      const results: any[] = res?.data?.attachments || res?.attachments || [];
      const failed = results.filter((r: any) => r && r.success === false);
      await refetchAttachments();
      addNotification?.({
        type: failed.length ? 'warning' : 'success',
        title: failed.length ? 'Some files were not added' : 'Attachment added',
        message: failed.length
          ? `${results.length - failed.length} of ${results.length} file(s) added.`
          : `${files.length} file(s) attached to this correspondence.`
      });
    } catch (error: any) {
      addNotification?.({
        type: 'error',
        title: 'Upload failed',
        message: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Could not add the file.'
      });
    } finally {
      setUploadingAttachments(false);
      setUploadPercent(0);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: string, filename: string) => {
    const isSource = String((correspondence as any)?.primaryAttachmentId || '') === String(attachmentId);
    const ok = await confirm({
      title: 'Remove this attachment?',
      message: `"${filename}" will be removed from this correspondence.${isSource ? '\n\nIt is currently the source document; you will need to pick another.' : ''}`,
      confirmText: 'Remove',
      variant: 'danger'
    });
    if (!ok) return;
    setDeletingAttachmentId(attachmentId);
    try {
      await apiClient.attachments.delete(attachmentId);
      await refetchAttachments();
      addNotification?.({ type: 'success', title: 'Attachment removed', message: filename });
    } catch (error: any) {
      addNotification?.({
        type: 'error',
        title: 'Could not remove attachment',
        message: error?.response?.data?.error || error?.message || 'Please try again.'
      });
    } finally {
      setDeletingAttachmentId(null);
    }
  };
  const { data: aiSummary, loading: loadingAI } = useCorrespondenceAI(
    correspondence && (correspondence as any).hasAISummary ? (correspondence._id || '') : null
  );

  // Handle attachment preview (open PDF in new tab with inline disposition)
  const handlePreviewAttachment = async (attachmentId: string) => {
    try {
      const res = await apiClient.attachments.getPresignedUrl(attachmentId, { inline: true });
      const url = (res as any)?.presignedUrl || (res as any)?.data?.presignedUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Ignore
    }
  };

  // Handle attachment download
  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    try {
      const blob = await apiClient.attachments.download(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Failed to download attachment:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      addNotification({ type: 'error', title: 'Download Failed', message: errorMsg });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !correspondence) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h3 className="text-red-800 dark:text-red-200 font-medium text-lg">Correspondence Not Found</h3>
            <p className="text-red-600 dark:text-red-300 text-sm mt-2">
              {error || 'This correspondence may have been deleted or you may not have permission to view it.'}
            </p>
            <div className="mt-4 flex space-x-3">
              <button
                onClick={() => navigate('/correspondences')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
              >
                Back to Correspondences
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check permissions
  const canDelete = user?.role === 'admin' || user?.role === 'implementor' ||
                    (user?.role === 'contributor' && (
                      (typeof correspondence.createdBy === 'string' && correspondence.createdBy === user?._id) ||
                      ((correspondence.createdBy as any)?._id === user?._id)
                    ));

  const from = (correspondence as any).from || { email: 'Unknown', name: '' };
  const to = (correspondence as any).to || [];
  const cc = (correspondence as any).cc || [];
  const content = correspondence.content || '';

  // Detect if content is HTML
  const isHtmlContent = content.trim().startsWith('<') && content.includes('</');

  // Strip HTML to plain text for display
  const getPlainText = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/correspondences')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {correspondence.subject || '(No Subject)'}
                  </h1>
                  {(correspondence as any).documentCode && (
                    <span className="flex-shrink-0 px-2.5 py-1 text-sm font-mono font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800">
                      {(correspondence as any).documentCode}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                  <span>{correspondence.documentType || 'email'}</span>
                  <span>•</span>
                  <span>{formatDate(correspondence.createdAt)}</span>
                  {correspondence.projectRelationship?.projectId && (
                    <>
                      <span>•</span>
                      <span className="text-blue-600 dark:text-blue-400">
                        {(correspondence.projectRelationship.projectId as any)?.projectName || 'Project'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            {canDelete && (
              <button
                onClick={() => {/* TODO: Add delete handler */}}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" role="tablist" aria-label="Correspondence details">
            {[
              { key: 'overview', label: 'Overview', icon: '📋' },
              { key: 'attachments', label: `Attachments (${attachments?.length ?? correspondence.attachmentCount ?? 0})`, icon: '📎' },
              { key: 'responses', label: `Responses (${(correspondence as any).responseSummary?.responseCount ?? 0})`, icon: '📄' },
              { key: 'email', label: 'Email Details', icon: '📧' },
              { key: 'ai', label: 'AI Summary', icon: '🤖' }
            ].map((tab) => (
              <button
                key={tab.key}
                role="tab"
                id={`tab-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`tabpanel-${tab.key}`}
                onClick={() => setActiveTab(tab.key as any)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div role="tabpanel" id="tabpanel-overview" aria-labelledby="tab-overview" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
            {/* From/To/CC */}
            <div className="space-y-3">
              {from && (
                <div className="grid grid-cols-12 gap-2">
                  <span className="col-span-1 text-sm font-medium text-gray-500 dark:text-gray-400">From:</span>
                  <span className="col-span-11 text-sm text-gray-900 dark:text-white">
                    {from.name ? `${from.name} <${from.email}>` : from.email}
                  </span>
                </div>
              )}
              {to.length > 0 && (
                <div className="grid grid-cols-12 gap-2">
                  <span className="col-span-1 text-sm font-medium text-gray-500 dark:text-gray-400">To:</span>
                  <span className="col-span-11 text-sm text-gray-900 dark:text-white">
                    {to.map((t: any) => t.name ? `${t.name} <${t.email}>` : t.email).join(', ')}
                  </span>
                </div>
              )}
              {cc.length > 0 && (
                <div className="grid grid-cols-12 gap-2">
                  <span className="col-span-1 text-sm font-medium text-gray-500 dark:text-gray-400">CC:</span>
                  <span className="col-span-11 text-sm text-gray-900 dark:text-white">
                    {cc.map((c: any) => c.name ? `${c.name} <${c.email}>` : c.email).join(', ')}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-12 gap-2">
                <span className="col-span-1 text-sm font-medium text-gray-500 dark:text-gray-400">Date:</span>
                <span className="col-span-11 text-sm text-gray-900 dark:text-white">
                  {(correspondence as any).sourceExtraction?.status === 'pending' && !(correspondence as any).documentDate
                    ? <span className="text-amber-600 dark:text-amber-400">Reading letter…</span>
                    : formatDate((correspondence as any).documentDate || correspondence.createdAt)}
                </span>
              </div>
              {(correspondence as any).sourceExtraction?.status === 'pending' && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  The letter is still being read (OCR). Its reference number, date and subject will fill in automatically when that finishes — nothing has been taken from the email.
                </div>
              )}
              {(correspondence as any).sourceExtraction?.status === 'failed' && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  The letter could not be read, so reference, date and subject were taken from the email. Please check them.
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Content
              </label>
              {isHtmlContent ? (
                <div
                  className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md overflow-auto max-h-96"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              ) : (
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-md max-h-96 overflow-auto">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {content || '(No content)'}
                  </p>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Document Type
                </label>
                <div className="text-sm text-gray-900 dark:text-white capitalize">
                  {correspondence.documentType || 'N/A'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Status
                </label>
                <div className="text-sm text-gray-900 dark:text-white capitalize">
                  {(correspondence as any).correspondenceStatus || correspondence.status || 'active'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Created By
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {(correspondence.createdBy as any)?.username || 'Unknown'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Entry Point
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {correspondence.manual ? 'Manual' : 'Auto-classified'}
                </div>
              </div>
            </div>

            {/* Source document selector (Email body vs primary attachment). When classification
                finds >1 attachment, the correspondence is flagged needsPrimaryAttachmentSelection
                and we surface an orange banner prompting the user to pick one. */}
            {(() => {
              const atts = (attachments || []) as any[];
              const primaryId = (correspondence as any).primaryAttachmentId || null;
              const needsPick = !!(correspondence as any).needsPrimaryAttachmentSelection;
              const primaryAtt = primaryId ? atts.find(a => (a._id || a.id) === primaryId) : null;

              return (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                      Source Document
                    </label>
                    {savingPrimary && (
                      <span className="text-xs text-gray-400">Saving…</span>
                    )}
                  </div>

                  {needsPick && atts.length > 1 && (
                    <div className="mb-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                      This correspondence has {atts.length} attachments. Please pick which one is the primary source (e.g. the cover letter).
                    </div>
                  )}

                  <div className="text-sm text-gray-900 dark:text-white mb-2">
                    {primaryAtt ? (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="truncate max-w-[220px]" title={primaryAtt.filename}>{primaryAtt.filename}</span>
                      </span>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-300">Email body</span>
                    )}
                  </div>

                  {atts.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <select
                        value={primaryId || ''}
                        onChange={(e) => handleSetPrimaryAttachment(e.target.value || null)}
                        disabled={savingPrimary}
                        className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      >
                        <option value="">Email body (default)</option>
                        {atts.map((a: any) => (
                          <option key={a._id || a.id} value={a._id || a.id}>
                            {a.filename}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Tags */}
            {correspondence.tags && correspondence.tags.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {correspondence.tags.map((tag: string, i: number) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Editable Custom Fields */}
            {orgCustomFields.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Custom Fields</label>
                  {!editingCustomFields ? (
                    <button
                      onClick={() => {
                        const initial: Record<string, any> = {};
                        const existing = correspondence.customFields || [];
                        orgCustomFields.forEach((fd: any) => {
                          const ef = existing.find((cf: any) => cf.fieldId === fd.fieldId);
                          initial[fd.fieldId] = ef?.value ?? fd.defaultValue ?? (fd.type === 'multi-select' ? [] : '');
                        });
                        setCustomFieldsDraft(initial);
                        setEditingCustomFields(true);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingCustomFields(false)}
                        className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        disabled={savingFields}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={async () => {
                          setSavingFields(true);
                          const fields = orgCustomFields.map((fd: any) => ({
                            fieldId: fd.fieldId,
                            name: fd.name,
                            type: fd.type,
                            value: customFieldsDraft[fd.fieldId] ?? null,
                          }));
                          try {
                            await updateCorrespondence({ id: correspondence._id || '', data: { customFields: fields } });
                            await refetchCorrespondence();
                            setEditingCustomFields(false);
                          } catch (err) {
                            console.error('Failed to save custom fields:', err);
                          } finally {
                            setSavingFields(false);
                          }
                        }}
                        disabled={savingFields}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {savingFields ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {!editingCustomFields ? (
                  <div className="grid grid-cols-2 gap-3">
                    {(correspondence.customFields || [])
                      .filter((field: any) => field.value !== null && field.value !== undefined && field.value !== '' && field.fieldId !== 'document_age_days')
                      .map((field: any, i: number) => (
                      <div key={i}>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{field.name}:</span>
                        <span className="ml-2 text-sm text-gray-900 dark:text-white">
                          {Array.isArray(field.value)
                            ? field.value.map((v: string, idx: number) => (
                                <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 mr-1">
                                  {v}
                                </span>
                              ))
                            : field.type === 'date'
                              ? (() => { try { return formatDate(new Date(field.value), false); } catch { return String(field.value); } })()
                              : String(field.value)}
                        </span>
                      </div>
                    ))}
                    {(correspondence.customFields || []).filter((f: any) => f.value !== null && f.value !== undefined && f.value !== '' && f.fieldId !== 'document_age_days').length === 0 && (
                      <p className="text-sm text-gray-400 dark:text-gray-500 italic col-span-2">No custom fields set. Click Edit to add.</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {orgCustomFields.map((fd: any) => {
                      const val = customFieldsDraft[fd.fieldId];
                      const inputClass = "w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
                      return (
                        <div key={fd.fieldId}>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                            {fd.name}{fd.required && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          {fd.type === 'select' ? (
                            <select value={val || ''} onChange={(e) => setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: e.target.value }))} className={inputClass}>
                              <option value="">—</option>
                              {(fd.options || []).map((opt: any) => <option key={opt.value} value={opt.value}>{opt.label || opt.value}</option>)}
                            </select>
                          ) : fd.type === 'multi-select' ? (
                            <div className="flex flex-wrap gap-1">
                              {(fd.options || []).map((opt: any) => {
                                const selected = Array.isArray(val) ? val : [];
                                const isSelected = selected.includes(opt.value);
                                return (
                                  <button key={opt.value} type="button"
                                    onClick={() => {
                                      const next = isSelected ? selected.filter((v: string) => v !== opt.value) : [...selected, opt.value];
                                      setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: next }));
                                    }}
                                    className={cn(
                                      "px-2 py-1 rounded text-xs border transition-colors",
                                      isSelected
                                        ? "bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300"
                                        : "bg-gray-50 border-gray-200 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400 hover:bg-gray-100"
                                    )}>
                                    {opt.label || opt.value}
                                  </button>
                                );
                              })}
                            </div>
                          ) : fd.type === 'date' ? (
                            <input type="date" value={val ? String(val).substring(0, 10) : ''} onChange={(e) => setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: e.target.value }))} className={inputClass} />
                          ) : fd.type === 'boolean' ? (
                            <select value={val === true ? 'true' : val === false ? 'false' : ''} onChange={(e) => setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: e.target.value === 'true' ? true : e.target.value === 'false' ? false : null }))} className={inputClass}>
                              <option value="">—</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : fd.type === 'number' || fd.type === 'currency' ? (
                            <input type="number" value={val ?? ''} onChange={(e) => setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: e.target.value ? Number(e.target.value) : '' }))} className={inputClass} step="any" />
                          ) : (
                            <input type="text" value={val ?? ''} onChange={(e) => setCustomFieldsDraft(prev => ({ ...prev, [fd.fieldId]: e.target.value }))} className={inputClass} placeholder={fd.placeholder || ''} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'attachments' && (
          <div role="tabpanel" id="tabpanel-attachments" aria-labelledby="tab-attachments" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Attachments ({attachments?.length ?? correspondence.attachmentCount ?? 0})
              </h2>
              {canManageAttachments && (
                <>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleAddAttachments}
                    disabled={uploadingAttachments}
                  />
                  <button
                    type="button"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachments}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded text-sm font-medium"
                  >
                    {uploadingAttachments ? 'Uploading…' : 'Add attachment'}
                  </button>
                </>
              )}
            </div>
            {uploadingAttachments && (
              <div className="mb-4">
                <UploadProgress percent={uploadPercent} />
              </div>
            )}
            {loadingAttachments ? (
              <div className="text-center py-8">
                <div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading attachments...</p>
              </div>
            ) : attachments && attachments.length > 0 ? (
              <div className="space-y-3">
                {attachments.map((attachment: any, index: number) => {
                  const attachmentId = attachment._id || attachment.id;
                  const hasOCR = !!(attachment.extractedContent || attachment.ocrData?.extractedText);

                  return (
                    <div
                      key={attachmentId || index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <svg className="w-8 h-8 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {attachment.filename || `Attachment ${index + 1}`}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {attachment.contentType} • {((attachment.size || 0) / 1024).toFixed(1)} KB
                            </p>
                            {hasOCR && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-1">OCR text available</p>
                            )}
                            {(attachment.extractedContent || attachment.ocrData?.extractedText) && (
                              <details className="mt-2">
                                <summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                                  View extracted text
                                </summary>
                                <div className="mt-2">
                                  <div className="flex items-center justify-end mb-1">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const text = attachment.extractedContent || attachment.ocrData?.extractedText || '';
                                        navigator.clipboard.writeText(text);
                                        const btn = document.getElementById(`copy-btn-detail-${attachmentId}`);
                                        if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }
                                      }}
                                      id={`copy-btn-detail-${attachmentId}`}
                                      className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 transition-colors"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <div className="bg-gray-100 dark:bg-gray-900 rounded max-h-96 overflow-y-auto">
                                    {(attachment.extractedContent || attachment.ocrData?.extractedText || '').split(/(?=---\s*Page\s+\d+\s*---)/).map((section: string, sIdx: number) => {
                                      const pageMatch = section.match(/^---\s*Page\s+(\d+)\s*---\n?/);
                                      const content = pageMatch ? section.replace(/^---\s*Page\s+\d+\s*---\n?/, '') : section;
                                      const trimmed = content.replace(/\n{3,}/g, '\n\n').trim();
                                      if (!trimmed) return null;
                                      return (
                                        <div key={sIdx}>
                                          {pageMatch && (
                                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 sticky top-0">
                                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                              </svg>
                                              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                Page {pageMatch[1]}
                                              </span>
                                            </div>
                                          )}
                                          <div className="text-xs text-gray-700 dark:text-gray-300 p-3 whitespace-pre-wrap leading-relaxed">
                                            {trimmed}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 flex gap-2">
                          {(attachment.contentType?.includes('pdf') || attachment.filename?.toLowerCase().endsWith('.pdf')) && (
                            <button
                              onClick={() => handlePreviewAttachment(attachmentId)}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium whitespace-nowrap"
                            >
                              Preview
                            </button>
                          )}
                          <button
                            onClick={() => handleDownloadAttachment(attachmentId, attachment.filename || 'attachment')}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium whitespace-nowrap"
                          >
                            Download
                          </button>
                          {canManageAttachments && (
                            <button
                              onClick={() => handleDeleteAttachment(attachmentId, attachment.filename || 'attachment')}
                              disabled={deletingAttachmentId === attachmentId}
                              className="px-3 py-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 rounded text-xs font-medium whitespace-nowrap"
                            >
                              {deletingAttachmentId === attachmentId ? 'Removing…' : 'Remove'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No attachments available
              </p>
            )}
          </div>
        )}

        {activeTab === 'email' && (
          <div role="tabpanel" id="tabpanel-email" aria-labelledby="tab-email" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Email Details
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                    {from.name ? `${from.name} <${from.email}>` : from.email || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                    {formatDate((correspondence as any).documentDate || correspondence.createdAt)}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                  {to.length > 0
                    ? to.map((t: any) => t.name ? `${t.name} <${t.email}>` : t.email).join('; ')
                    : 'N/A'}
                </div>
              </div>
              {cc.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                    {cc.map((c: any) => c.name ? `${c.name} <${c.email}>` : c.email).join('; ')}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Type</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded capitalize">
                    {correspondence.documentType || 'email'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Direction</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded capitalize">
                    {(correspondence as any).direction || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Preserved</label>
                  <div className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50 p-2 rounded">
                    {(correspondence as any).hasEmailContent ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              {/* Source Email ID */}
              {(correspondence as any).emailId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Email ID</label>
                  <div className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-900/50 p-2 rounded break-all">
                    {typeof (correspondence as any).emailId === 'object' ? (correspondence as any).emailId._id : (correspondence as any).emailId}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'responses' && (
          <div role="tabpanel" id="tabpanel-responses" aria-labelledby="tab-responses" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Responses
            </h2>
            {(() => {
              const rs = (correspondence as any).responseSummary;
              const responses: Array<any> = rs?.responses || [];
              if (responses.length === 0) {
                return (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No responses have been created for this correspondence yet.
                  </p>
                );
              }
              return (
                <div className="space-y-3">
                  {responses.map((r: any) => {
                    const rid = r.responseId?._id || r.responseId;
                    const statusColor = r.status === 'Approved' || r.status === 'approved' || r.status === 'closed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : r.status === 'Rejected' || r.status === 'rejected'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                    return (
                      <div
                        key={rid}
                        onClick={() => navigate(`/responses/${rid}`)}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {r.subject || 'Untitled Response'}
                            </h3>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={cn('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', statusColor)}>
                                {r.status || 'Draft'}
                              </span>
                              {r.currentStage && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Stage: {r.currentStage}
                                </span>
                              )}
                              {r.isPrimary && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Primary</span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 flex-shrink-0">
                            {r.createdAt ? formatDate(r.createdAt) : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'ai' && (
          <div role="tabpanel" id="tabpanel-ai" aria-labelledby="tab-ai" className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              AI Summary
            </h2>
            {loadingAI ? (
              <div className="text-center py-8">
                <div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading AI summary...</p>
              </div>
            ) : aiSummary ? (
              <div className="space-y-4">
                {aiSummary.summary && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Summary
                    </label>
                    <div className="prose dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-900/50 p-4 rounded">
                      {aiSummary.summary}
                    </div>
                  </div>
                )}
                {aiSummary.keyTopics && aiSummary.keyTopics.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Key Topics
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {aiSummary.keyTopics.map((topic: string, i: number) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {aiSummary.sentiment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sentiment
                    </label>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {aiSummary.sentiment.overall || aiSummary.sentiment}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                {(correspondence as any).hasAISummary ? 'Failed to load AI summary' : 'No AI summary available for this correspondence'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CorrespondenceDetailPage;
